import { useEffect, useState } from 'react';
import type { SystemId } from '../emulator/types';
import { cheatFormatHint, parseCheatLines } from '../emulator/cheats/codec';
import {
  addCheat,
  deleteCheat,
  listCheats,
  updateCheat,
  type CheatRecord,
} from '../storage/cheatRepository';
import { useStore } from '../state/store';

export interface CheatManagerProps {
  romId: string;
  system: SystemId;
  /** チートの内容が変わったら呼ばれる。セッションへ反映する。 */
  onChange: () => void;
  /** コアがチートに対応していない場合 */
  unsupported?: boolean;
}

export function CheatManager({ romId, system, onChange, unsupported }: CheatManagerProps) {
  const toast = useStore((s) => s.toast);
  const [cheats, setCheats] = useState<CheatRecord[]>([]);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');

  const reload = () => void listCheats(romId).then(setCheats);
  useEffect(reload, [romId]);

  // 入力中のコードをその場で検証する。コアへ渡す前に打ち間違いを見せる。
  const parsed = code.trim() ? parseCheatLines(code, system) : [];
  const invalid = parsed.filter((p) => !p.valid);

  const submit = async () => {
    if (parsed.length === 0) return;
    if (invalid.length > 0) {
      toast(invalid[0]!.error ?? 'コードが正しくありません', 'error');
      return;
    }
    await addCheat(romId, label, parsed.map((p) => p.normalized).join('\n'));
    setLabel('');
    setCode('');
    setAdding(false);
    reload();
    onChange();
  };

  const toggle = async (cheat: CheatRecord) => {
    await updateCheat({ ...cheat, enabled: !cheat.enabled });
    reload();
    onChange();
  };

  const remove = async (cheat: CheatRecord) => {
    await deleteCheat(cheat.id);
    reload();
    onChange();
  };

  return (
    <div className="space-y-2">
      {unsupported && (
        <p className="rounded-lg border border-amber-900/60 bg-amber-950/40 p-2 text-[0.65rem] leading-relaxed text-amber-300">
          このコアはチートに対応していません。登録はできますが効果はありません。
        </p>
      )}

      {cheats.length === 0 && !adding && (
        <p className="py-4 text-center text-xs text-slate-500">チートは登録されていません</p>
      )}

      {cheats.map((cheat) => (
        <div
          key={cheat.id}
          className="flex items-start gap-2 rounded-xl border border-base-600 bg-base-900 p-2.5"
        >
          <input
            type="checkbox"
            checked={cheat.enabled}
            onChange={() => void toggle(cheat)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-accent"
            aria-label={`${cheat.label} を有効にする`}
          />
          <div className="min-w-0 flex-1">
            <p className={`truncate text-xs ${cheat.enabled ? 'text-slate-200' : 'text-slate-500'}`}>
              {cheat.label}
            </p>
            <p className="truncate font-mono text-[0.6rem] text-slate-600">
              {cheat.code.split('\n').join(' / ')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void remove(cheat)}
            className="shrink-0 px-1 text-xs text-red-400"
            aria-label={`${cheat.label} を削除`}
          >
            ✕
          </button>
        </div>
      ))}

      {adding ? (
        <div className="space-y-2 rounded-xl border border-base-600 bg-base-900 p-2.5">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="名前（例: 無限ライフ）"
            className="w-full rounded-lg border border-base-600 bg-base-800 px-2 py-1.5 text-xs outline-none focus:border-accent"
          />
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={cheatFormatHint(system)}
            rows={3}
            className="w-full rounded-lg border border-base-600 bg-base-800 px-2 py-1.5 font-mono text-xs outline-none focus:border-accent"
          />

          {/* 解析結果をその場で見せる。効かないコードの原因を探るときの手がかりになる。 */}
          {parsed.length > 0 && (
            <ul className="space-y-0.5 text-[0.6rem]">
              {parsed.map((p, i) => (
                <li key={i} className={p.valid ? 'text-slate-500' : 'text-red-400'}>
                  <span className="font-mono">{p.normalized}</span>
                  {p.valid
                    ? p.decoded?.address !== undefined
                      ? ` → $${p.decoded.address.toString(16).toUpperCase()} = ${p.decoded.value?.toString(16).toUpperCase()}`
                      : ' → 書式 OK'
                    : ` — ${p.error}`}
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="btn-ghost !min-h-0 !py-2 !text-xs"
              onClick={() => {
                setAdding(false);
                setLabel('');
                setCode('');
              }}
            >
              取消
            </button>
            <button
              type="button"
              className="btn-primary !min-h-0 !py-2 !text-xs"
              disabled={parsed.length === 0 || invalid.length > 0}
              onClick={() => void submit()}
            >
              追加
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn-ghost w-full !min-h-0 !py-2 !text-xs" onClick={() => setAdding(true)}>
          + チートを追加
        </button>
      )}
    </div>
  );
}
